// import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks/reduxHooks';
import { UserRole } from '../types/auth';
import DashboardStats from '../components/dashboard/DashboardStats';
import PreapprovalChart from '../components/dashboard/PreapprovalChart';
import OrdersChart from '../components/dashboard/OrdersChart';
// import PartnerDistribution from '../components/dashboard/PartnerDistribution';
import MerchantKycStatus from '../components/dashboard/MerchantKycStatus';
// import ActivityFeed from '../components/dashboard/ActivityFeed';
import MerchantsByLender from '../components/dashboard/MerchantsByLender';
import { AlertTriangle, EyeIcon, Settings, ListFilter, Users, Store, PackageOpen, Layers, ShoppingBag, FileText, BanknoteIcon } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAppSelector(state => state.auth);
  
  // Function to group permissions by category
  const groupPermissionsByCategory = (permissions: string[] = []) => {
    // Filter out dashboard permissions and then separate view and manage
    const filteredPermissions = permissions.filter(p => !p.includes('dashboard'));
    const viewPermissions = filteredPermissions.filter(p => p.startsWith('view-'));
    const managePermissions = filteredPermissions.filter(p => p.startsWith('manage-'));
    
    return { viewPermissions, managePermissions };
  };
  
  // Format permission name for display
  const formatPermissionName = (permission: string) => {
    // Remove the 'view-' or 'manage-' prefix and replace hyphens with spaces
    const name = permission.replace(/^(view|manage)-/, '').replace(/-/g, ' ');
    
    // Capitalize each word
    return name.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };
  
  // Get icon for permission
  const getPermissionIcon = (permission: string) => {
    const permissionName = permission.replace(/^(view|manage)-/, '');
    
    switch (permissionName) {
      case 'dashboard': return <Layers className="h-4 w-4" />;
      case 'partner': return <Users className="h-4 w-4" />;
      case 'merchant': return <Store className="h-4 w-4" />;
      case 'product': return <PackageOpen className="h-4 w-4" />;
      case 'scheme': return <ListFilter className="h-4 w-4" />;
      case 'brand': return <Store className="h-4 w-4" />;
      case 'order': return <ShoppingBag className="h-4 w-4" />;
      case 'pre-approvals-report': return <FileText className="h-4 w-4" />;
      case 'settlements-report': return <BanknoteIcon className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };
  
  // Check if user has the CleverPeUser role
  if (user?.role === UserRole.CLEVERPE_USER) {
    const { viewPermissions, managePermissions } = groupPermissionsByCategory(user.dashPermissions);
    
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto bg-white border border-yellow-200 rounded-lg shadow-lg overflow-hidden">
          <div className="bg-yellow-50 p-4 flex items-center border-b border-yellow-200">
            <div className="bg-yellow-100 p-2 rounded-full mr-3">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-800">Access Information</h3>
          </div>
          
          <div className="p-6">
            {/* Dashboard restriction message */}
            <div className="mb-6 bg-yellow-50 p-4 rounded-md border border-yellow-200">
              <p className="text-yellow-800 font-medium mb-1">Dashboard Access Restricted</p>
              <p className="text-sm text-gray-700">
                Dashboard access is not available for CleverPe User accounts. This is a system restriction that cannot be overridden by permissions.
              </p>
            </div>
            
            <p className="text-gray-700 mb-4">
              Welcome to CleverPe! As a CleverPe User, you have specific access permissions tailored to your role.
              {user.groups && user.groups.length > 0 && (
                <span> You are part of the following groups category: <span className="font-medium">{user.groups.join(', ')}</span>.</span>
              )}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* View Permissions */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <div className="flex items-center mb-2">
                  <EyeIcon className="h-4 w-4 text-blue-600 mr-2" />
                  <h4 className="text-base font-medium text-blue-800">You Can View</h4>
                </div>
                
                {viewPermissions.length > 0 ? (
                  <ul className="flex flex-wrap gap-1">
                    {viewPermissions.map((permission, index) => (
                      <li key={index} className="inline-flex items-center bg-white rounded-md p-2 shadow-sm text-sm">
                        <span className="flex-shrink-0 text-blue-500 mr-2">
                          {getPermissionIcon(permission)}
                        </span>
                        <span className="text-gray-700">{formatPermissionName(permission)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600">No view permissions assigned.</p>
                )}
              </div>
              
              {/* Manage Permissions */}
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <div className="flex items-center mb-2">
                  <Settings className="h-4 w-4 text-green-600 mr-2" />
                  <h4 className="text-base font-medium text-green-800">You Can Manage</h4>
                </div>
                
                {managePermissions.length > 0 ? (
                  <ul className="flex flex-wrap gap-1">
                    {managePermissions.map((permission, index) => (
                      <li key={index} className="inline-flex items-center bg-white rounded-md p-2 shadow-sm text-sm">
                        <span className="flex-shrink-0 text-green-500 mr-2">
                          {getPermissionIcon(permission)}
                        </span>
                        <span className="text-gray-700">{formatPermissionName(permission)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600">No management permissions assigned.</p>
                )}
              </div>
            </div>
            
            <div className="mt-4 bg-gray-50 p-3 rounded-md border border-gray-200">
              <p className="text-sm text-gray-600">
                Please use the sidebar navigation to access these features. If you need additional access, please contact your administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <DashboardStats />
      
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PreapprovalChart />
        {/* <PartnerDistribution /> */}
        <MerchantsByLender />
      </div>
      
      <div className="mt-8 grid grid-cols-1 gap-6">
        <OrdersChart />
      </div>
      
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MerchantKycStatus />
        {/* <ActivityFeed /> */}
      </div>
    </>
  );
};

export default Dashboard; 